/**
 * gameEventBus.js
 *
 * Central event bus for game events that can trigger immersive theme transitions.
 * The AI-generated theme can register handlers for these events to play visual effects
 * (screen flashes, particle bursts, camera shakes, overlays, etc.)
 *
 * Events are dispatched as CustomEvents on the document, so the sandboxed theme JS
 * can listen for them via parentDocument.addEventListener('game:event_name', handler).
 *
 * === EVENT CATALOG ===
 *
 * game:round_start        - A new round has begun
 *   detail: { roundNumber, roundType, competitiveRound }
 *
 * game:round_end          - A round has finished
 *   detail: { roundNumber, roundType }
 *
 * game:your_turn           - It's the current player's turn
 *   detail: { playerName, requestId, timeoutMs }
 *
 * game:turn_submitted      - Current player submitted their action
 *   detail: { action }
 *
 * game:turn_expired        - A player's turn timed out
 *   detail: { playerName, isYou }
 *
 * game:voting_started      - Voting round has begun
 *   detail: { roundNumber, duration }
 *
 * game:vote_cast           - A vote was cast (live update)
 *   detail: { voterName, targetName, votesIn, totalVoters }
 *
 * game:vote_results        - Vote results revealed
 *   detail: { voteCounts, eliminatedPlayerName, roundNumber }
 *
 * game:player_eliminated   - A player was eliminated
 *   detail: { playerName, playerId, isYou }
 *
 * game:player_joined       - A new player joined the game
 *   detail: { playerName, isHuman }
 *
 * game:game_started        - Game has started (ready check complete)
 *   detail: { playerCount }
 *
 * game:game_completed      - Game is over
 *   detail: { finalRankings }
 *
 * game:new_message         - A new chat message was received
 *   detail: { senderName, messageType, isHuman }
 *
 * game:typing_started      - RETIRED 08-14 (never emitted anymore): generated
 *   themes rendered their own stuck "X is typing..." chrome off this event
 *   (the generator docs never paired it with typing_stopped). The constant
 *   stays so old references don't throw; the TypingIndicator pill is the one
 *   thinking surface.
 *
 * game:typing_stopped      - RETIRED 08-14, same story as typing_started
 *
 * game:glitch_reveal       - Imposter/glitch identity revealed to player
 *   detail: { isGlitch, topic }
 *
 * game:glitch_result       - Glitch game result (caught or escaped)
 *   detail: { result, glitchPlayerName, topic }
 *
 * game:ready_up            - A player readied up
 *   detail: { readyCount, totalHumans, playerNames }
 *
 * game:player_speaking     - It's a specific player's turn to speak
 *   detail: { playerName, isHuman, isAI }
 */

const GAME_EVENTS = {
  ROUND_START: 'game:round_start',
  ROUND_END: 'game:round_end',
  YOUR_TURN: 'game:your_turn',
  TURN_SUBMITTED: 'game:turn_submitted',
  TURN_EXPIRED: 'game:turn_expired',
  VOTING_STARTED: 'game:voting_started',
  VOTE_CAST: 'game:vote_cast',
  VOTE_RESULTS: 'game:vote_results',
  PLAYER_ELIMINATED: 'game:player_eliminated',
  PLAYER_JOINED: 'game:player_joined',
  GAME_STARTED: 'game:game_started',
  GAME_COMPLETED: 'game:game_completed',
  NEW_MESSAGE: 'game:new_message',
  TYPING_STARTED: 'game:typing_started',
  TYPING_STOPPED: 'game:typing_stopped',
  GLITCH_REVEAL: 'game:glitch_reveal',
  GLITCH_RESULT: 'game:glitch_result',
  READY_UP: 'game:ready_up',
  PLAYER_SPEAKING: 'game:player_speaking',
  MUSIC_URL_CHANGED: 'game:music_url_changed',
  // Space master mute (Nina 08-02): the space's sound button silences ALL
  // audio sources — LofiRadio unmounts its iframe, the watch party theater
  // mutes (one-way; unmute is a deliberate tap on the theater's own control)
  SPACE_AUDIO_MUTED: 'game:space_audio_muted',
  // Watch party / space TV playback — LofiRadio pauses itself while true
  WATCH_PARTY_PLAYING: 'game:watch_party_playing',
  // 🎬🍿 09-23 movie-night batch 1 (MOVIE_NIGHT_UX_HANDOFF §3.6, AUX_DUCK): a
  // SCREENING is seating/playing in the house. LofiRadio ducks its bed for
  // EVERYONE on it, seated or not (WATCH_PARTY_PLAYING only ever fires for a
  // seated, audible viewer). Its own event on purpose: the party event fires
  // on every theater state change and on unmount, and would race the wrap.
  SCREENING_LIVE: 'game:screening_live',
  // 🌕🚪 09-26: the Go on an event's start card was pressed ({ room, id }). The
  // card lives deep in the memoized feed; GameStepPage listens and walks the
  // viewer into that room's picture (the same two-step as every other Go).
  EVENT_GO: 'game:event_go',
  // Live competitive whodunnit — LofiRadio hides entirely while true (the
  // scenario ambience beds own the audio; the FAB stacked over the
  // investigation stage's timer/case-file corner)
  NOIR_CASE_ACTIVE: 'game:noir_case_active',
  // Whodunnit STAGE 1 takeover on screen — LofiRadio tucks its FAB + panel
  // offscreen but stays MOUNTED (scene-jazz packs keep playing; unmounting
  // would kill the audio). Distinct from NOIR_CASE_ACTIVE, which unmounts.
  NOIR_STAGE_UP: 'game:noir_stage_up',
  // 🏡⋯ A ported room's place page (W2d): hide LofiRadio's FAB (the ⋯ menu is
  // the door) · open its panel from the menu's "Music". The music plays on.
  MUSIC_FAB_HIDDEN: 'game:music_fab_hidden',
  MUSIC_OPEN: 'game:music_open',
};

/**
 * Dispatch a game event as a CustomEvent on the document.
 * Theme JS in the sandbox can listen via: parentDocument.addEventListener('game:event_name', handler)
 */
function emitGameEvent(eventName, detail = {}) {
  try {
    const event = new CustomEvent(eventName, {
      detail: { ...detail, timestamp: Date.now() },
      bubbles: true,
    });
    document.dispatchEvent(event);
  } catch (err) {
    // Non-fatal — don't break game if event dispatch fails
    console.warn(`[GameEventBus] Failed to emit ${eventName}:`, err.message);
  }
}

export { GAME_EVENTS, emitGameEvent };
